import { create } from 'zustand';
import { ApiError, DriverApiClient, type SessionPort } from '../api/client';
import type {
  AuthTokens,
  DriverActionRequest,
  DriverActionResult,
  DriverRouteToday,
  DriverStop,
  FailureReasonKey,
} from '../api/types';
import { createStorage, type DeliveryStorage } from '../db/storage';
import { newClientActionId } from '../lib/id';
import { nowIso } from '../lib/time';
import { QueueEngine } from './queue';
import {
  allHandled,
  applyAction,
  currentStop,
  findStopById,
  mergeActionResult,
  revertStopInSnapshot,
} from './route';
import type {
  PersistedScreen,
  PersistedUiState,
  Prefs,
  QueueItem,
  RecordActionInput,
  ScreenName,
  SessionState,
} from './types';
import { DEFAULT_PREFS } from './types';

export const UNDO_WINDOW_MS = 15_000;

export type SheetName =
  | 'pickup'
  | 'notTaken'
  | 'complete'
  | 'logout'
  | 'install'
  | null;

export type RouteLoadState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export type LoginError = 'invalid' | 'noRole' | 'offline' | null;

export type ScanPurpose = 'load' | 'deliver';

/**
 * Waarom de app naar handmatig invoeren is teruggevallen (F7). De reden reist via de
 * store en niet via lokale state: de scanner navigeert wég op het moment dat hij de reden
 * kent, dus hij kan hem zelf niet meer tonen.
 */
export type ManualEntryReason = 'cameraDenied' | 'cameraFailed' | null;

export type ScanOutcomeKind =
  | 'delivered'
  | 'loaded'
  | 'alreadyLoaded'
  | 'alreadyDelivered'
  | 'otherStop'
  | 'unknown';

export interface ScanOutcome {
  kind: ScanOutcomeKind;
  code: string;
  stopId: string | null;
  parcelNumber: string | null;
  partnerName: string | null;
  city: string | null;
  clock: string;
  /** Het wachtrij-item dat hierbij hoort, voor "Toch niet? Draai terug". */
  clientActionId: string | null;
  /** Er staat nog een pakket open op dit bezoek (E4). */
  moreParcelsOnStop: boolean;
}

export interface FailDraft {
  reason: FailureReasonKey | null;
  note: string;
  invoiceAnyway: boolean;
  redeliver: boolean;
}

export const EMPTY_FAIL_DRAFT: FailDraft = {
  reason: null,
  note: '',
  invoiceAnyway: false,
  redeliver: false,
};

export interface FailResult {
  stopId: string;
  partnerName: string;
  stopOrder: number;
  reason: FailureReasonKey;
  invoiceAnyway: boolean;
  /** De schakelaar van H2. De dátum komt van de server (A-04) en landt in de rit-cache. */
  redeliver: boolean;
  clientActionId: string;
}

interface AppState {
  ready: boolean;
  online: boolean;
  session: SessionState | null;
  route: DriverRouteToday | null;
  fetchedAt: string | null;
  routeState: RouteLoadState;
  screen: ScreenName;
  stopId: string | null;
  sheet: SheetName;
  queue: QueueItem[];
  prefs: Prefs;
  loginError: LoginError;
  loggingIn: boolean;
  scanPurpose: ScanPurpose;
  scanOutcome: ScanOutcome | null;
  manualEntryReason: ManualEntryReason;
  failDraft: FailDraft;
  failResult: FailResult | null;
  /** Pakketnummer waarvoor de "Ligt er niet"-sheet openstaat. */
  notTakenParcel: string | null;
  installPromptAvailable: boolean;

  init: () => Promise<void>;
  setOnline: (online: boolean) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshRoute: (force?: boolean) => Promise<void>;
  navigate: (screen: ScreenName, stopId?: string | null) => void;
  back: () => void;
  openSheet: (sheet: SheetName) => void;
  closeSheet: () => void;
  setPrefs: (changes: Partial<Prefs>) => Promise<void>;
  recordAction: (input: RecordActionInput) => Promise<QueueItem>;
  undo: (clientActionId: string) => Promise<void>;
  setScanPurpose: (purpose: ScanPurpose) => void;
  setScanOutcome: (outcome: ScanOutcome | null) => void;
  setManualEntryReason: (reason: ManualEntryReason) => void;
  setFailDraft: (changes: Partial<FailDraft>) => void;
  resetFailDraft: () => void;
  setFailResult: (result: FailResult | null) => void;
  setNotTakenParcel: (parcelNumber: string | null) => void;
  setInstallPromptAvailable: (available: boolean) => void;
  flushQueue: () => Promise<void>;
  tryQueueNow: () => Promise<void>;
}

let storage: DeliveryStorage = createStorage();
let api: DriverApiClient;
let queueEngine: QueueEngine;

const PERSISTED_SCREENS: PersistedScreen[] = ['day', 'load', 'ride', 'stop', 'return', 'done'];

function isPersistedScreen(screen: ScreenName): screen is PersistedScreen {
  return (PERSISTED_SCREENS as ScreenName[]).includes(screen);
}

function sessionFromTokens(tokens: AuthTokens): SessionState {
  const name = [tokens.user.firstName, tokens.user.lastName].filter(Boolean).join(' ').trim();
  return {
    accessToken: tokens.accessToken,
    accessTokenExpiresAt: tokens.accessTokenExpiresAt,
    refreshToken: tokens.refreshToken,
    refreshTokenExpiresAt: tokens.refreshTokenExpiresAt,
    driverName: name.length > 0 ? name : tokens.user.email,
    email: tokens.user.email,
  };
}

export const useAppStore = create<AppState>((set, get) => {
  const sessionPort: SessionPort = {
    getAccessToken: () => get().session?.accessToken ?? null,
    getRefreshToken: () => get().session?.refreshToken ?? null,
    onRefreshed: async (tokens) => {
      const session = sessionFromTokens(tokens);
      set({ session });
      await storage.writeSession(session);
    },
    onSessionLost: () => {
      // De wachtrij blijft staan; hij verdraagt een verlopen sessie en gaat door na inloggen.
      set({ session: null, screen: 'login' });
      void storage.writeSession(null);
    },
  };

  api = new DriverApiClient(sessionPort);

  async function persistUi(screen: ScreenName, stopId: string | null): Promise<void> {
    if (!isPersistedScreen(screen)) return;
    const ui: PersistedUiState = { screen, stopId };
    await storage.writeUi(ui);
  }

  async function applyResult(result: DriverActionResult): Promise<void> {
    const route = get().route;
    if (!route) return;
    const merged = mergeActionResult(route, result);
    set({ route: merged });
    await storage.writeRoute({ snapshot: merged, fetchedAt: get().fetchedAt ?? merged.fetchedAt });
  }

  queueEngine = new QueueEngine({
    storage,
    get api() {
      return api;
    },
    isOnline: () => get().online,
    now: () => Date.now(),
    onItemsChanged: (items) => set({ queue: items }),
    onResult: applyResult,
  });

  return {
    ready: false,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    session: null,
    route: null,
    fetchedAt: null,
    routeState: 'idle',
    screen: 'login',
    stopId: null,
    sheet: null,
    queue: [],
    prefs: { ...DEFAULT_PREFS },
    loginError: null,
    loggingIn: false,
    scanPurpose: 'deliver',
    scanOutcome: null,
    manualEntryReason: null,
    failDraft: { ...EMPTY_FAIL_DRAFT },
    failResult: null,
    notTakenParcel: null,
    installPromptAvailable: false,

    async init() {
      const [session, cached, ui, prefs] = await Promise.all([
        storage.readSession(),
        storage.readRoute(),
        storage.readUi(),
        storage.readPrefs(),
      ]);
      await queueEngine.load();

      set({
        session,
        prefs,
        route: cached?.snapshot ?? null,
        fetchedAt: cached?.fetchedAt ?? null,
        routeState: cached ? 'ready' : 'idle',
        screen: session ? (ui?.screen ?? 'day') : 'login',
        stopId: ui?.stopId ?? null,
        ready: true,
      });

      if (session) {
        // Verversen bij elk verbindingsmoment als vangnet (contract §2.3).
        if (get().online) void api.refresh().catch(() => undefined);
        void get().refreshRoute();
        void queueEngine.flush();
      }
    },

    setOnline(online) {
      const wasOffline = !get().online;
      set({ online });
      if (online && wasOffline && get().session) {
        void api.refresh().catch(() => undefined);
        void queueEngine.flush();
        void get().refreshRoute();
      }
    },

    async login(email, password) {
      if (!get().online) {
        set({ loginError: 'offline' });
        return;
      }
      set({ loggingIn: true, loginError: null });
      try {
        const tokens = await api.login(email, password);
        if (!tokens.user.roles.includes('Driver')) {
          set({ loggingIn: false, loginError: 'noRole' });
          return;
        }
        const session = sessionFromTokens(tokens);
        await storage.writeSession(session);
        set({ session, loggingIn: false, loginError: null, screen: 'day', stopId: null });
        await persistUi('day', null);
        await get().refreshRoute(true);
        void queueEngine.flush();
      } catch (error) {
        const code = error instanceof ApiError ? error.code : 'UNKNOWN';
        const status = error instanceof ApiError ? error.status : 0;
        const noRole = code === 'DRIVER_ROLE_REQUIRED' || code === 'DRIVER_NOT_LINKED';
        set({
          loggingIn: false,
          loginError: noRole ? 'noRole' : status === 0 ? 'offline' : 'invalid',
        });
      }
    },

    async logout() {
      await storage.writeSession(null);
      await storage.writeRoute(null);
      set({
        session: null,
        route: null,
        fetchedAt: null,
        routeState: 'idle',
        screen: 'login',
        stopId: null,
        sheet: null,
        scanOutcome: null,
        failResult: null,
        manualEntryReason: null,
      });
    },

    async refreshRoute(force = false) {
      const state = get();
      if (!state.session || !state.online) return;
      // Na "Ophalen starten" ligt de rit vast (D53): dan alleen nog uit de cache lezen.
      const closed =
        state.route !== null &&
        (state.route.route.status === 'inProgress' || state.route.route.status === 'completed');
      if (closed && !force) return;

      if (!state.route) set({ routeState: 'loading' });
      try {
        const snapshot = await api.getRouteToday();
        set({ route: snapshot, fetchedAt: snapshot.fetchedAt, routeState: 'ready' });
        await storage.writeRoute({ snapshot, fetchedAt: snapshot.fetchedAt });
      } catch (error) {
        if (error instanceof ApiError && error.code === 'NO_ROUTE_TODAY') {
          set({ route: null, routeState: 'empty' });
          await storage.writeRoute(null);
          return;
        }
        if (!get().route) set({ routeState: 'error' });
      }
    },

    navigate(screen, stopId) {
      const nextStopId = stopId === undefined ? get().stopId : stopId;
      set({
        screen,
        stopId: nextStopId,
        sheet: null,
        // De terugvalreden hoort bij hét scherm handmatig invoeren en verdwijnt zodra de
        // bezorger daar weg navigeert. Wissen bij het verlaten in plaats van in een
        // opruimeffect: dat laatste zou onder StrictMode de reden meteen weer wegpoetsen.
        manualEntryReason: screen === 'manual' ? get().manualEntryReason : null,
      });
      void persistUi(screen, nextStopId);
    },

    back() {
      const { screen, route } = get();
      switch (screen) {
        case 'scanner':
        case 'manual':
        case 'fail':
          get().navigate('stop');
          return;
        case 'scanresult':
        case 'failconfirm':
        case 'stop':
          get().navigate(route && allHandled(route) ? 'return' : 'ride');
          return;
        case 'queue':
        case 'settings':
          get().navigate('day');
          return;
        case 'load':
        case 'ride':
        case 'return':
        case 'done':
          get().navigate('day');
          return;
        default:
          return;
      }
    },

    openSheet(sheet) {
      set({ sheet });
    },

    closeSheet() {
      set({ sheet: null, notTakenParcel: null });
    },

    async setPrefs(changes) {
      const prefs = { ...get().prefs, ...changes };
      set({ prefs });
      await storage.writePrefs(prefs);
    },

    async recordAction(input) {
      const state = get();
      const route = state.route;
      if (!route) throw new Error('Geen rit in de cache.');

      const occurredAt = input.request.occurredAt ?? nowIso();
      const request: DriverActionRequest = {
        clientActionId: newClientActionId(),
        type: input.type,
        occurredAt,
        ...input.request,
      };

      const previousStop: DriverStop | null =
        request.type === 'stop.deliver' || request.type === 'stop.fail'
          ? findStopById(route, request.stopId ?? null)
          : null;

      const next = applyAction(route, request);
      set({ route: next });
      await storage.writeRoute({ snapshot: next, fetchedAt: state.fetchedAt ?? next.fetchedAt });

      const item: QueueItem = {
        clientActionId: request.clientActionId,
        seq: await storage.nextSeq(),
        routeId: route.route.id,
        type: request.type,
        request,
        occurredAt,
        state: 'waiting',
        attempts: 0,
        nextAttemptAt: Date.now(),
        subject: input.subject ?? null,
        error: null,
        errorCode: null,
        sentAt: null,
        revertStop: previousStop,
      };
      await queueEngine.enqueue(item);
      return item;
    },

    async undo(clientActionId) {
      const item = get().queue.find((i) => i.clientActionId === clientActionId);
      if (!item) return;
      const removed = await queueEngine.remove(clientActionId);
      if (!removed) return;
      const route = get().route;
      if (route && item.revertStop) {
        const reverted = revertStopInSnapshot(route, item.revertStop);
        set({ route: reverted });
        await storage.writeRoute({
          snapshot: reverted,
          fetchedAt: get().fetchedAt ?? reverted.fetchedAt,
        });
      }
      set({ scanOutcome: null, failResult: null });
    },

    setScanPurpose(purpose) {
      set({ scanPurpose: purpose });
    },

    setScanOutcome(outcome) {
      set({ scanOutcome: outcome });
    },

    setManualEntryReason(reason) {
      set({ manualEntryReason: reason });
    },

    setFailDraft(changes) {
      set({ failDraft: { ...get().failDraft, ...changes } });
    },

    resetFailDraft() {
      set({ failDraft: { ...EMPTY_FAIL_DRAFT } });
    },

    setFailResult(result) {
      set({ failResult: result });
    },

    setNotTakenParcel(parcelNumber) {
      set({ notTakenParcel: parcelNumber });
    },

    setInstallPromptAvailable(available) {
      set({ installPromptAvailable: available });
    },

    async flushQueue() {
      await queueEngine.flush();
    },

    async tryQueueNow() {
      await queueEngine.tryNow();
    },
  };
});

/** Alleen voor tests: verwissel de opslag vóór `init()`. */
export function __setStorageForTests(next: DeliveryStorage): void {
  storage = next;
}

export function getQueueEngine(): QueueEngine {
  return queueEngine;
}

export function getApiClient(): DriverApiClient {
  return api;
}

/** Afgeleide waarden die meerdere schermen delen. */
export function selectCurrentStop(state: AppState): DriverStop | null {
  return state.route ? currentStop(state.route) : null;
}

export function selectQueueCount(state: AppState): number {
  return state.queue.filter((item) => item.state !== 'sent').length;
}
